import React, { useState } from 'react';
import { useWorkflowMetadata } from '../contexts/WorkflowMetadataContext';
import './WorkflowMetadataTab.css';

interface MetadataSection {
  title: string;
  items: Array<{
    label: string;
    value: string | number | boolean | null | undefined;
    copyable?: boolean;
    type?: 'id' | 'time' | 'text' | 'boolean' | 'number';
  }>;
}

interface WorkflowMetadataTabProps {
  sessionId: string;
  contextId: string | null | undefined;
  scriptId?: string;
  currentPhase?: number;
}

const WorkflowMetadataTab: React.FC<WorkflowMetadataTabProps> = ({ sessionId, contextId, scriptId, currentPhase }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { metadata } = useWorkflowMetadata();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(label);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatValue = (value: any, type?: string): string => {
    if (value === null || value === undefined) {
      return '—';
    }

    switch (type) {
      case 'time':
        return new Date(value).toLocaleString();
      case 'boolean':
        return value ? '✓' : '✗';
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      case 'id':
        return value.length > 12 ? `${value.substring(0, 12)}...` : value;
      default:
        return String(value);
    }
  };

  const renderSection = (section: MetadataSection) => (
    <div className="metadata-section" key={section.title}>
      <h3 className="metadata-section-title">{section.title}</h3>
      <div className="metadata-items">
        {section.items.map((item, index) => (
          <div className="metadata-item" key={index}>
            <span className="metadata-label">{item.label}:</span>
            <span className={`metadata-value ${item.type === 'id' ? 'metadata-id' : ''}`}>
              {formatValue(item.value, item.type)}
              {item.copyable && item.value && (
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(String(item.value), item.label)}
                  title="Copy to clipboard"
                >
                  {copiedId === item.label ? '✓' : '📋'}
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const sections: MetadataSection[] = [];

  // Session Information (always available)
  sections.push({
    title: 'Session Information',
    items: [
      { label: 'Session ID', value: sessionId || metadata?.session?.id, copyable: true, type: 'id' },
      { label: 'Started', value: metadata?.session?.startedAt, type: 'time' },
      { label: 'User', value: metadata?.session?.userId, type: 'text' },
      { label: 'Tenant', value: metadata?.session?.tenantId, type: 'text' }
    ]
  });

  // Workflow Details
  if (metadata?.workflow) {
    sections.push({
      title: 'Workflow Details',
      items: [
        { label: 'Context ID', value: metadata.workflow.contextId, copyable: true, type: 'id' },
        { label: 'Workflow ID', value: metadata.workflow.workflowId, copyable: true, type: 'id' },
        { label: 'Script ID', value: metadata.workflow.scriptId, copyable: true, type: 'id' },
        { label: 'Phase', value: metadata.workflow.phase, type: 'number' },
        { label: 'Status', value: metadata.workflow.status, type: 'text' }
      ]
    });
  }

  // Task Identification
  if (metadata?.taskIdentification) {
    sections.push({
      title: 'Task Identification',
      items: [
        { label: 'Domain', value: metadata.taskIdentification.domain, type: 'text' },
        { label: 'Task', value: metadata.taskIdentification.task, type: 'text' },
        { label: 'Method', value: metadata.taskIdentification.method, type: 'text' },
        { label: 'Identified At', value: metadata.taskIdentification.identifiedAt, type: 'time' },
        { label: 'Response Time', value: metadata.taskIdentification.responseTimeMs ? `${metadata.taskIdentification.responseTimeMs}ms` : '—', type: 'text' },
        { label: 'API Calls', value: metadata.taskIdentification.apiCalls, type: 'number' },
        { label: 'Cache Hit', value: metadata.taskIdentification.cacheHit, type: 'boolean' }
      ]
    });
  }

  // Knowledge Base - Enhanced with detailed framework and domain rules
  if (metadata?.knowledgeBase) {
    const kb = metadata.knowledgeBase as any;
    
    // Main Knowledge Base section
    const kbItems: MetadataSection['items'] = [];
    
    // Task Type
    if (kb.task_type) {
      kbItems.push({ label: 'Task Type', value: kb.task_type, type: 'text' });
    }
    
    // Framework details
    if (kb.framework) {
      kbItems.push({ label: 'Framework', value: kb.framework.name || kb.framework_name || 'N/A', type: 'text' });
      if (kb.framework.domains?.length > 0) {
        kbItems.push({ label: 'Domains', value: kb.framework.domains.join(', '), type: 'text' });
      }
      if (kb.framework.primary_domain) {
        kbItems.push({ label: 'Primary Domain', value: kb.framework.primary_domain, type: 'text' });
      }
    } else if (kb.framework_name) {
      kbItems.push({ label: 'Framework', value: kb.framework_name, type: 'text' });
    }
    
    // Domain Rules count
    if (kb.domainRules && Array.isArray(kb.domainRules)) {
      kbItems.push({ label: 'Domain Rules Applied', value: kb.domainRules.length, type: 'number' });
    } else if (kb.domainRulesCount !== undefined) {
      kbItems.push({ label: 'Domain Rules Applied', value: kb.domainRulesCount, type: 'number' });
    }
    
    // Other KB info
    kbItems.push({ label: 'Safety Checks', value: kb.safetyChecks, type: 'boolean' });
    if (kb.customizations?.length > 0) {
      kbItems.push({ label: 'Customizations', value: kb.customizations.length, type: 'number' });
    }
    
    sections.push({
      title: 'Knowledge Base',
      items: kbItems
    });
    
    // Detailed Framework Information (if available)
    if (kb.framework && (kb.framework.description || kb.framework.purpose)) {
      const frameworkDetailItems: MetadataSection['items'] = [];
      
      if (kb.framework.description) {
        frameworkDetailItems.push({ 
          label: 'Description', 
          value: kb.framework.description.substring(0, 200) + (kb.framework.description.length > 200 ? '...' : ''), 
          type: 'text' 
        });
      }
      
      if (kb.framework.purpose) {
        frameworkDetailItems.push({ 
          label: 'Purpose', 
          value: kb.framework.purpose.substring(0, 200) + (kb.framework.purpose.length > 200 ? '...' : ''), 
          type: 'text' 
        });
      }
      
      sections.push({
        title: 'Task Framework Details',
        items: frameworkDetailItems
      });
    }
    
    // Domain Rules Details (if available)
    if (kb.domainRules && Array.isArray(kb.domainRules) && kb.domainRules.length > 0) {
      const domainRuleItems: MetadataSection['items'] = [];
      
      kb.domainRules.forEach((rule: any, index: number) => {
        if (rule.name) {
          domainRuleItems.push({
            label: `Rule ${index + 1}`,
            value: rule.name,
            type: 'text'
          });
        }
      });
      
      if (domainRuleItems.length > 0) {
        sections.push({
          title: 'Domain Rules Applied',
          items: domainRuleItems
        });
      }
    }
    
    // Learned Patterns (if available)
    if (kb.learned_patterns && Array.isArray(kb.learned_patterns) && kb.learned_patterns.length > 0) {
      const patternItems: MetadataSection['items'] = [];
      
      kb.learned_patterns.slice(0, 5).forEach((pattern: any, index: number) => {
        const patternText = typeof pattern === 'string' ? pattern : (pattern.pattern || pattern.description || 'Pattern');
        patternItems.push({
          label: `Pattern ${index + 1}`,
          value: patternText.substring(0, 100) + (patternText.length > 100 ? '...' : ''),
          type: 'text'
        });
      });
      
      sections.push({
        title: 'Learned Patterns',
        items: patternItems
      });
    }
  }

  // Performance Metrics
  if (metadata?.performance || metadata?.taskIdentification) {
    const perfItems: MetadataSection['items'] = [];
    
    if (metadata?.performance) {
      perfItems.push(
        { label: 'Total Latency', value: metadata.performance?.totalLatencyMs ? `${metadata.performance.totalLatencyMs}ms` : 'N/A', type: 'text' },
        { label: 'API Calls', value: (metadata.performance as any)?.apiCalls || 0, type: 'number' },
        { label: 'Cache Hits', value: (metadata.performance as any)?.cacheHits || 0, type: 'number' },
        { label: 'Cache Hit Rate', value: (metadata.performance as any)?.cacheHitRate ? `${((metadata.performance as any).cacheHitRate * 100).toFixed(1)}%` : '0%', type: 'text' }
      );
      
      if ((metadata.performance as any)?.tokensUsed) {
        perfItems.push(
          { label: 'Tokens Used', value: (metadata.performance as any).tokensUsed, type: 'number' },
          { label: 'Cost Estimate', value: `$${(((metadata.performance as any).tokensUsed / 1000) * 0.03).toFixed(4)}`, type: 'text' }
        );
      }
      
      if (metadata.performance?.breakdownMs) {
        Object.entries(metadata.performance.breakdownMs).forEach(([key, value]) => {
          perfItems.push({
            label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            value: `${value}ms`,
            type: 'text' as const
          });
        });
      }
    }
    
    // Add task identification metrics
    if (metadata?.taskIdentification && (metadata.taskIdentification as any)?.responseTimeMs) {
      perfItems.push(
        { label: 'Task ID Time', value: `${(metadata.taskIdentification as any).responseTimeMs}ms`, type: 'text' }
      );
    }

    if (perfItems.length > 0) {
      sections.push({
        title: 'Performance Metrics',
        items: perfItems
      });
    }
  }

  // Comprehensive Audit Trail
  const auditItems: MetadataSection['items'] = [];
  
  // Basic audit info
  if (metadata?.audit) {
    auditItems.push(
      { label: 'Created By', value: metadata.audit.createdBy || 'System', type: 'text' },
      { label: 'Created At', value: metadata.audit.createdAt, type: 'time' }
    );
    
    if (metadata.audit.lastModified) {
      auditItems.push(
        { label: 'Last Modified', value: metadata.audit.lastModified, type: 'time' },
        { label: 'Modified By', value: metadata.audit.modifiedBy || 'System', type: 'text' }
      );
    }
    
    if (metadata.audit.version) {
      auditItems.push(
        { label: 'Version', value: metadata.audit.version, type: 'number' }
      );
    }
    
    if ((metadata.audit as any)?.ipAddress) {
      auditItems.push(
        { label: 'IP Address', value: (metadata.audit as any).ipAddress, type: 'text' }
      );
    }
  }
  
  // Workflow events
  if (metadata?.workflow && (metadata.workflow as any)?.events && Array.isArray((metadata.workflow as any).events)) {
    (metadata.workflow as any).events.forEach((event: any, index: number) => {
      const timestamp = new Date(event.timestamp).toLocaleTimeString();
      auditItems.push({
        label: `[${timestamp}]`,
        value: `${event.type}: ${event.description || event.message}`,
        type: 'text'
      });
    });
  }
  
  // User actions
  if ((metadata as any)?.userActions && Array.isArray((metadata as any)?.userActions)) {
    (metadata as any)?.userActions.forEach((action: any) => {
      const timestamp = new Date(action.timestamp).toLocaleTimeString();
      auditItems.push({
        label: `[${timestamp}] ${action.type}`,
        value: action.description,
        type: 'text'
      });
    });
  }
  
  // Task override history
  if (metadata?.taskIdentification && (metadata.taskIdentification as any)?.overrideHistory && Array.isArray((metadata.taskIdentification as any)?.overrideHistory)) {
    (metadata.taskIdentification as any)?.overrideHistory.forEach((override: any, index: number) => {
      const timestamp = new Date(override.timestamp).toLocaleTimeString();
      auditItems.push({
        label: `[${timestamp}] Override`,
        value: `${override.from?.domain}/${override.from?.task} → ${override.to?.domain}/${override.to?.task}`,
        type: 'text'
      });
    });
  }
  
  // Interrupt history
  if ((metadata as any)?.interrupt && (metadata as any)?.interrupt?.history && Array.isArray((metadata as any)?.interrupt?.history)) {
    (metadata as any)?.interrupt?.history.forEach((interrupt: any) => {
      const timestamp = new Date(interrupt.timestamp).toLocaleTimeString();
      auditItems.push({
        label: `[${timestamp}] Interrupt`,
        value: interrupt.reason || 'User interrupted workflow',
        type: 'text'
      });
    });
  }
  
  if (auditItems.length > 0) {
    sections.push({
      title: 'Audit Trail',
      items: auditItems
    });
  }

  const exportMetadata = () => {
    const dataStr = JSON.stringify(metadata, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `metadata_${Date.now()}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const copyAllMetadata = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(metadata, null, 2));
      setCopiedId('all');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy metadata:', err);
    }
  };

  return (
    <div className="workflow-metadata-tab">
      <div className="metadata-header">
        <h2 className="metadata-title">Workflow Metadata</h2>
        <div className="metadata-actions">
          <button 
            className="metadata-action-btn"
            onClick={copyAllMetadata}
            title="Copy all metadata"
          >
            {copiedId === 'all' ? '✓ Copied' : 'Copy All'}
          </button>
          <button 
            className="metadata-action-btn"
            onClick={exportMetadata}
            title="Export as JSON"
          >
            Export JSON
          </button>
        </div>
      </div>
      
      <div className="metadata-content">
        {sections.length > 0 ? (
          sections.map(section => renderSection(section))
        ) : (
          <div className="metadata-empty">
            <p>No metadata available yet. Start a conversation to see session information.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowMetadataTab;